import { Application } from '@feathersjs/feathers'

import { User } from '../domain/user'
import {
  CreditApplication,
  CreditApplicationStatus,
  ApplicationDoc,
  GENERIC,
  NOT_APPROVED,
  REVIEW_ERROR,
  CreditApplicationDetails
} from '../domain/creditApplication'
import { UserCredentials } from '../domain/identity'
import {
  ApplicationProgress,
  Step,
  APPLICATION_PROGRESS,
  ENTER_PROMO,
  ALMOST_THERE,
  handlerFormState,
  FormState,
  SubmissionPayload,
  ApplicationSubmissionState,
  GET_PHYSICAL,
  SAFE_AND_SECURE
} from '../components/ApplicationProgress/types'
import { CreditApplicationError } from '../components/ApplicationProgress/ApplicationSubmitted/types'

import { AlmostThereFormValues } from '../components/ApplicationProgress/AlmostThere/types'
import { PromoCodeValues } from '../components/ApplicationProgress/PromoCode/types'
import { PersonalInFormationFormValues } from '../components/ApplicationProgress/PersonalInformation/types'
import moment from 'moment'
import { PersonalQuestionValues } from '../components/ApplicationProgress/Address/types'
import { stripCharactersFromPhone } from './formhelper.service'
import { mapUserPatchFields, patchUser } from './user.service'
import { postBranchLogEvent, BRANCH_LOG_EVENTS } from './branch.service'
import { deviceSessionId } from './socure.service'
import {
  generateAddressNextButtonEvent,
  generateAddressScreenEvent,
  generateAlmostThereScreenEvent,
  generatePersonalInformatButtonEvent,
  generatePersonalInformationScreenEvent,
  generatePromotionNextButtonEvent,
  generateValidatedPromotionEvent,
  generatePromotionScreenEvent
} from './amplitude.service'
import { BranchUrlParameters } from '../domain/branch'

export const findApplications =
  (app: Application) => (): Promise<CreditApplication[]> => {
    return app.service('credit/applications').find()
  }

export const getApplicationStatus =
  (app: Application) =>
  (id: string): Promise<CreditApplicationDetails> =>
    app.service('credit/applications').get(id)

export const submitApplication =
  (app: Application) =>
  (payload: SubmissionPayload): Promise<CreditApplication> =>
    app.service('credit/applications').create(payload)

export const getApplicationDoc =
  (app: Application) =>
  (appId: string, docId: string): Promise<ApplicationDoc> =>
    app.service(`credit/applications/${appId}/documents`).get(docId)

export const declineApplicationCreditOffer =
  (app: Application) =>
  (applicationId: string): Promise<CreditApplication> =>
    app
      .service('credit/applications')
      .patch(applicationId, { status: CreditApplicationStatus.declined })

export const hasCompletedCreditApplication = (
  creditApps: CreditApplication[]
): boolean =>
  creditApps.some(
    (creditapp) => creditapp.status === CreditApplicationStatus.accepted
  )

export const hasApplicationWaiting = (
  creditApps: CreditApplication[]
): CreditApplication | null =>
  creditApps.find(
    (creditApp) => creditApp.status === CreditApplicationStatus.waiting
  ) || null

export const hasApplicationSubmitted = (
  creditApps: CreditApplication[]
): CreditApplication | null =>
  creditApps.filter(
    (creditApp) => creditApp.status !== CreditApplicationStatus.accepted
  )[0] || null

export const getSubmittedApplication =
  (creditApps: CreditApplication[]) =>
  (id: string): CreditApplication | null =>
    creditApps.find((creditApp) => creditApp.id === id) || null

export const hasApplicationRejected = (
  creditApp: CreditApplication | null
): CreditApplicationError | null => {
  if (!creditApp) return null
  if (isErrorStatus(creditApp.status))
    return mapCreditApplicationError(creditApp.status)
  return null
}

/**
 * Gets passed in step
 * @param step
 * @param steps
 * @returns
 */
export const getApplicationStep = (step: number, steps: Step[]): Step =>
  steps.filter((appStep) => step === appStep.step)[0]

// reuse this?
const promotionStep = (progress: ApplicationProgress) =>
  getApplicationStep(
    ENTER_PROMO.step,
    progress.steps
  ) as ApplicationSubmissionState<PromoCodeValues>

export const setPreviousStep = (nextStep: number): number =>
  nextStep === ENTER_PROMO.step ? ENTER_PROMO.step : nextStep - 1

export const sendTrackingEventsForSteps = (
  progress: ApplicationProgress
): void => {
  const currentStep = progress.currentStep.step
  const prevStep = progress.prevStep

  if (isPromotionScreenEvent(currentStep, prevStep)) {
    generatePromotionScreenEvent()
  }
  if (isPromotionNextButtonEvent(progress)) {
    generatePromotionNextButtonEvent()
    generateValidatedPromotionEvent(getValidatedPromocode(progress))
  }
  if (isGetPhysicalScreenEvent(currentStep, prevStep)) {
    generateAddressScreenEvent()
  }
  if (isGetPhysicalButtonEvent(currentStep, prevStep)) {
    generateAddressNextButtonEvent()
  }
  if (isPersonalInformationScreenEvent(currentStep, prevStep)) {
    generatePersonalInformationScreenEvent()
  }
  if (isPersonalInformationButtonEvent(currentStep, prevStep)) {
    generatePersonalInformatButtonEvent()
  }
  if (isAlmostThereScreenEvent(currentStep, prevStep)) {
    generateAlmostThereScreenEvent()
  }
}

const isPromotionScreenEvent = (
  currentStep: number,
  prevStep: number
): boolean => ENTER_PROMO.step === currentStep && prevStep === ENTER_PROMO.step

const isPromotionNextButtonEvent = (progress: ApplicationProgress): boolean => {
  const promoStep = promotionStep(progress)
  const currentStep = progress.currentStep.step
  const prevStep = progress.prevStep
  return (
    ENTER_PROMO.step === prevStep &&
    currentStep === GET_PHYSICAL.step &&
    !!promoStep.formState.promocode
  )
}

const getValidatedPromocode = (progress: ApplicationProgress): string =>
  promotionStep(progress).formState.promocode

const isGetPhysicalScreenEvent = (
  currentStep: number,
  prevStep: number
): boolean => ENTER_PROMO.step === prevStep && currentStep === GET_PHYSICAL.step

const isGetPhysicalButtonEvent = (
  currentStep: number,
  prevStep: number
): boolean =>
  GET_PHYSICAL.step === prevStep && currentStep === SAFE_AND_SECURE.step

const isPersonalInformationScreenEvent = (
  currentStep: number,
  prevStep: number
): boolean =>
  GET_PHYSICAL.step === prevStep && currentStep === SAFE_AND_SECURE.step

const isPersonalInformationButtonEvent = (
  currentStep: number,
  prevStep: number
): boolean =>
  SAFE_AND_SECURE.step === prevStep && currentStep === ALMOST_THERE.step

const isAlmostThereScreenEvent = (
  currentStep: number,
  prevStep: number
): boolean =>
  SAFE_AND_SECURE.step === prevStep && currentStep === ALMOST_THERE.step

const prefillPromocodeStep = (promocodeStep: typeof ENTER_PROMO) => {
  const code = localStorage.getItem(BranchUrlParameters.PROMO_CODE)
  if (code) {
    return {
      ...promocodeStep,
      formState: {
        promocode: code
      }
    }
  }
  return promocodeStep
}

/**
 * Helper to determine UI state at first render
 * @param user
 * @returns
 */
export const determineInitialState = (
  user: User,
  userCredentials: UserCredentials
): ApplicationProgress => {
  if (userCredentials.verifiedPhone) {
    return {
      ...APPLICATION_PROGRESS,
      currentStep: prefillPromocodeStep(ENTER_PROMO),
      steps: seedSteps(user, userCredentials)
    }
  }

  return { ...APPLICATION_PROGRESS, steps: seedSteps(user, userCredentials) }
}

const seedSteps = (user: User, userCredentials: UserCredentials) => {
  return APPLICATION_PROGRESS.steps.map((step: Step) => {
    if (step.step === ALMOST_THERE.step) {
      return {
        ...step,
        formState: {
          ...step.formState,
          email: user.primaryEmail,
          primaryPhone: stripCharactersFromPhone(userCredentials.phoneNumber)
        }
      }
    }
    if (step.step === GET_PHYSICAL.step) {
      return {
        ...step,
        formState: {
          ...step.formState,
          name: {
            familyName: user.familyName,
            givenName: user.givenName,
            middleName: user.middleName
          }
        }
      }
    }
    return step
  })
}

/**
 * Helper to update current step
 * @param steps
 * @param updatedStep
 * @returns
 */
export const updateCurrentStep = (steps: Step[], updatedStep: Step): Step[] => {
  const lastStep = steps.find((s) => s.step === ALMOST_THERE.step)
  if (lastStep && updatedStep) {
    const updatedLastStep = updateLastStep(lastStep, updatedStep)

    return steps.map((s) => {
      if (s.step === updatedStep.step) {
        return { ...updatedStep }
      }
      if (s.step === updatedLastStep.step) {
        return { ...updatedLastStep }
      }
      return s
    })
  }

  return steps.map((s) => {
    if (s.step === updatedStep.step) {
      return { ...updatedStep }
    }
    return s
  })
}

/**
 * Helper to update the last step
 * @param updatedStep
 */
const updateLastStep = (lastStep: Step, updatedStep: Step): Step => {
  lastStep.formState = {
    ...lastStep.formState,
    ...isOfTypeByProperty(
      updatedStep,
      lastStep.formState as AlmostThereFormValues
    )
  }
  return lastStep
}

const isOfTypeByProperty = (
  updatedStep: Step,
  lastStep: AlmostThereFormValues
): Partial<AlmostThereFormValues> => {
  if (isAddressAndNameInformation(updatedStep.formState)) {
    return mapAddressAndNameInfo(
      updatedStep.formState as PersonalQuestionValues
    )
  }
  if (isPromoInformation(updatedStep.formState)) {
    return mapPromoInfo(updatedStep.formState as PromoCodeValues)
  }
  if (isSecureInformation(updatedStep.formState)) {
    return mapSecureInfo(updatedStep.formState as PersonalInFormationFormValues)
  }
  return lastStep
}

const isSecureInformation = (formState: handlerFormState): boolean => {
  if ('dob' in formState && 'ssn' in formState && 'annualIncome' in formState)
    return true
  return false
}

const isPromoInformation = (formState: handlerFormState): boolean => {
  if ('promocode' in formState) return true
  return false
}

const isAddressAndNameInformation = (formState: handlerFormState): boolean => {
  if ('name' in formState && 'address' in formState) return true
  return false
}

const mapAddressAndNameInfo = (
  values: FormState<PersonalQuestionValues>
): Partial<AlmostThereFormValues> => {
  const address = `${values.address.street} ${values.address.refinement}\n${values.address.city}, ${values.address.state} ${values.address.postal}`

  const legalName = `${values.name.givenName} ${values.name.middleName || ''} ${
    values.name.familyName || ''
  }`.trim()

  return {
    legalName,
    address
  }
}

const mapPromoInfo = (
  values: FormState<PromoCodeValues>
): Partial<AlmostThereFormValues> => {
  const promoCode = values.promocode

  return {
    promoCode
  }
}

const mapSecureInfo = (
  values: FormState<PersonalInFormationFormValues>
): Partial<AlmostThereFormValues> => {
  const dob = values.dob ? moment(values.dob).format('MM/DD/YYYY') : ''
  const ssn = values.ssn
  const annualIncome = values.annualIncome

  return {
    dob,
    ssn,
    annualIncome
  }
}

export const mapSubmissonPayload = (
  applicationProgress: ApplicationProgress
): SubmissionPayload => {
  const promoStep = promotionStep(applicationProgress)
  const almostDone = getApplicationStep(
    ALMOST_THERE.step,
    applicationProgress.steps
  ) as ApplicationSubmissionState<AlmostThereFormValues>
  const addressName = getApplicationStep(
    GET_PHYSICAL.step,
    applicationProgress.steps
  ) as ApplicationSubmissionState<PersonalQuestionValues>
  const safeSecure = getApplicationStep(
    SAFE_AND_SECURE.step,
    applicationProgress.steps
  ) as ApplicationSubmissionState<PersonalInFormationFormValues>

  return {
    deviceSessionId: deviceSessionId(),
    promoCode: promoStep.formState.promocode || almostDone.formState.promoCode,
    name: {
      first: addressName.formState.name.givenName,
      middle: addressName.formState.name.middleName || '',
      last: addressName.formState.name.familyName || '',
      suffix: ''
    },
    address: {
      street: addressName.formState.address.street,
      city: addressName.formState.address.city,
      state: addressName.formState.address.state,
      postal: addressName.formState.address.postal,
      country: addressName.formState.address.country,
      refinement: addressName.formState.address.refinement
    },
    birthdate: safeSecure.formState.dob
      ? moment(safeSecure.formState.dob).format('YYYY-MM-DD')
      : '',
    ssn: safeSecure.formState.ssn,
    income: safeSecure.formState.annualIncome,
    phone: almostDone.formState.primaryPhone,
    email: almostDone.formState.email,
    kycConsent: true // should always be true, otherwise cant click submit
  }
}

export const mapCreditApplicationError = (
  status: CreditApplicationStatus
): CreditApplicationError => {
  switch (status) {
    case CreditApplicationStatus.rejected:
      return NOT_APPROVED
    case CreditApplicationStatus.review:
      return REVIEW_ERROR
    case CreditApplicationStatus.errored:
      return GENERIC
    default:
      return GENERIC
  }
}

export const isErrorStatus = (status: CreditApplicationStatus): boolean => {
  return (
    isRejected(status) ||
    isGenericError(status) ||
    isReviewError(status) ||
    isExpired(status)
  )
}

export const isExpired = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.expired

export const isRejected = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.rejected

export const isGenericError = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.errored

export const isReady = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.ready

export const isWaiting = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.waiting

export const userDeclined = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.declined

export const isReviewError = (status: CreditApplicationStatus): boolean =>
  status === CreditApplicationStatus.review

/**
 * Submits Application and patch to User
 *
 * @param application
 * @param applicationProgress
 * @returns
 */
export const submitCreditApplication =
  (application: Application) =>
  async (payload: SubmissionPayload): Promise<CreditApplication> => {
    const submitCreditApp = submitApplication(application)
    const updateUser = patchUser(application)

    await updateUser(mapUserPatchFields(payload))
    const creditApplication = await submitCreditApp(payload)
    creditApplication && postBranchLogEvent(BRANCH_LOG_EVENTS.APP_SUBMITTED)

    return creditApplication
  }

/**
 *
 * @param application
 * @returns
 */
export const validatePromotionCode =
  (application: Application) =>
  ({ promocode }: PromoCodeValues): Promise<void> => {
    return application.service('/credit/promotions').get(promocode)
  }
